// src/Components/Modules/Inventory/Warehouses.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from '../../../AxiosInterceptors';
import Breadcrumb from '../../Partials/Breadcrumb';
import {
    Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, IconButton, InputAdornment
} from '@mui/material';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import Swal from 'sweetalert2';

export default function Warehouses() {
    const [warehouses, setWarehouses] = useState([]);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ warehouse_name: '', location: '', manager_name: '', manager_phone: '' });

    const fetchData = async () => {
        try {
            const res = await AxiosAuthInstance.get('warehouses', { params: { search } });
            setWarehouses(res.data.warehouses || []);
        } catch (err) { console.error(err); }
    };

    useEffect(() => { fetchData(); }, [search]);

    const openDialog = (warehouse = null) => {
        if (warehouse) {
            setEditing(warehouse);
            setForm({
                warehouse_name: warehouse.warehouse_name,
                location: warehouse.location || '',
                manager_name: warehouse.manager_name || '',
                manager_phone: warehouse.manager_phone || '',
            });
        } else {
            setEditing(null);
            setForm({ warehouse_name: '', location: '', manager_name: '', manager_phone: '' });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            const url = editing ? `warehouses/${editing.id}` : 'warehouses';
            const method = editing ? 'put' : 'post';
            await AxiosAuthInstance[method](url, form);
            Swal.fire({ icon: 'success', title: 'Success!', timer: 1500, showConfirmButton: false });
            setDialogOpen(false);
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    const handleToggle = async (id) => {
        try {
            await AxiosAuthInstance.post(`warehouses/${id}/toggle`);
            fetchData();
        } catch (err) { console.error(err); }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({ title: 'Delete Warehouse?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#f44336' });
        if (result.isConfirmed) {
            try {
                await AxiosAuthInstance.delete(`warehouses/${id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                fetchData();
            } catch (err) { console.error(err); }
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Warehouses" />
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight={700}>Warehouse Management</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField size="small" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><FaSearch /></InputAdornment> }} />
                    <Button variant="contained" startIcon={<FaPlus />} onClick={() => openDialog()}>Add Warehouse</Button>
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell>Warehouse Name</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Manager</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {warehouses.map((w) => (
                            <TableRow key={w.id} hover>
                                <TableCell><strong>{w.warehouse_name}</strong></TableCell>
                                <TableCell>{w.location || '-'}</TableCell>
                                <TableCell>{w.manager_name || '-'}</TableCell>
                                <TableCell>{w.manager_phone || '-'}</TableCell>
                                <TableCell align="center">
                                    <Chip label={w.is_active ? 'Active' : 'Inactive'} color={w.is_active ? 'success' : 'default'} size="small" />
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton size="small" onClick={() => handleToggle(w.id)} sx={{ color: w.is_active ? '#4caf50' : '#9e9e9e' }}>
                                        {w.is_active ? <FaToggleOn size={20} /> : <FaToggleOff size={20} />}
                                    </IconButton>
                                    <IconButton size="small" onClick={() => openDialog(w)} sx={{ color: '#1976d2' }}><FaEdit /></IconButton>
                                    <IconButton size="small" onClick={() => handleDelete(w.id)} sx={{ color: '#f44336' }}><FaTrash /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Edit Warehouse' : 'Add New Warehouse'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Warehouse Name *" value={form.warehouse_name} onChange={(e) => setForm({...form, warehouse_name: e.target.value})} fullWidth required />
                        <TextField label="Location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} fullWidth />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Manager Name" value={form.manager_name} onChange={(e) => setForm({...form, manager_name: e.target.value})} fullWidth />
                            <TextField label="Manager Phone" value={form.manager_phone} onChange={(e) => setForm({...form, manager_phone: e.target.value})} fullWidth />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}